<template>
    <div class="edit-chapter-container">
      <div class="page-header">
        <div class="header-with-back">
          <button @click="goBackToBook" class="back-button">
            <font-awesome-icon :icon="['fas', 'arrow-left']" /> Kembali ke Buku
          </button>
          <div>
            <h2>Edit Bab</h2>
            <p class="subtitle">Perbarui konten bab Anda</p>
          </div>
        </div>
      </div>
  
      <div v-if="isLoading" class="loading-container">
        <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
        <p>Memuat data bab...</p>
      </div>
      
      <div v-else class="form-container">
        <ChapterForm
          :initial-data="chapterData"
          :errors="errors"
          :is-submitting="isSubmitting"
          submit-button-text="Perbarui Bab"
          @save="saveChapter"
          @cancel="goBackToBook"
        />
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import ChapterForm from '@/components/ChapterForm.vue';
  import Swal from 'sweetalert2';
  
  export default {
    name: 'EditChapter',
    components: {
      ChapterForm
    },
    data() {
      return {
        chapterId: null,
        bookId: null,
        chapterSlug: '',
        bookSlug: '',
        chapterData: {
          title: '',
          order_number: 1,
          content: ''
        },
        errors: {},
        isSubmitting: false,
        isLoading: true
      };
    },
    async created() {
      this.bookSlug = this.$route.params.bookSlug;
      this.chapterSlug = this.$route.params.chapterSlug;
      this.bookId = parseInt(this.$route.params.bookId);
      
      // Muat data chapter yang akan diedit
      await this.loadChapterData();
    },
    methods: {
      async loadChapterData() {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            this.$router.push('/login');
            return;
          }
          
          const response = await axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/api/chapters/${this.chapterSlug}/`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          // Isi form dengan data dari API
          this.chapterData = {
            title: response.data.title || '',
            order_number: response.data.order_number || 1,
            content: response.data.content || ''
          };
          
          this.chapterId = response.data.id;
        } catch (error) {
          console.error('Error loading chapter data:', error);
          this.showNotification('error', 'Gagal memuat data bab. Silakan coba lagi.');
          
          // Kembali ke halaman buku jika data tidak bisa dimuat
          this.goBackToBook();
        } finally {
          this.isLoading = false;
        }
      },
      
      async saveChapter(formData) {
        this.errors = {};
        this.isSubmitting = true;
        
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            this.$router.push('/login');
            return;
          }
          
          // Siapkan payload untuk API
          const payload = {
            ...formData,
            book: this.bookSlug
          };
          
          // Update chapter yang sudah ada
          await axios.put(
            `${process.env.VUE_APP_API_BASE_URL}/api/chapters/${this.chapterSlug}/`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          this.showNotification('success', 'Bab berhasil diperbarui');
          
          // Kembali ke halaman buku
          this.goBackToBook();
        } catch (error) {
          console.error('Error updating chapter:', error);
          
          // Handle API error responses
          if (error.response?.data) {
            const responseData = error.response.data;
            const newErrors = {};
            
            Object.keys(responseData).forEach(key => {
              if (typeof responseData[key] === 'string') {
                newErrors[key] = responseData[key];
              } else if (Array.isArray(responseData[key])) {
                newErrors[key] = responseData[key][0];
              }
            });
            
            this.errors = newErrors;
          }
          
          this.showNotification('error', 'Gagal memperbarui bab. Silakan periksa form dan coba lagi.');
        } finally {
          this.isSubmitting = false;
        }
      },
      
      goBackToBook() {
        // Check if we came from CreateBook view
        if (this.$route.query.fromCreateBook) {
          // Redirect to EditBook route
          this.$router.push({
            name: 'EditBook',
            params: {
              bookSlug: this.bookSlug,
              bookId: this.bookId
            }
          });
        } else {
          // Default to book detail view
          this.$router.push(`/book/${this.bookSlug}`);
        }
      },
        
      showNotification(type, message) {
        Swal.fire({
          icon: type,
          title: type === 'success' ? 'Berhasil!' : 'Error!',
          text: message,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        });
      }
    }
  };
  </script>
  
  <style scoped>
  .edit-chapter-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 20px;
  }
  
  .page-header {
    text-align: center;
    margin-bottom: 30px;
  }
  
  .header-with-back {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    gap: 20px;
  }
  
  .back-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #f1f1f1;
    color: #333;
    border: none;
    padding: 10px 15px;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.3s;
  }
  
  .back-button:hover {
    background-color: #e0e0e0;
  }
  
  .page-header h2 {
    font-size: 28px;
    color: #333;
    margin-bottom: 10px;
  }
  
  .subtitle {
    color: #666;
    font-size: 16px;
  }
  
  .form-container {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
    padding: 30px;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
    padding: 60px 30px;
  }
  
  .loading-icon {
    font-size: 36px;
    color: #488e71;
    margin-bottom: 20px;
  }
  
  @media (max-width: 768px) {
    .edit-chapter-container {
      padding: 15px;
    }
    
    .form-container, 
    .loading-container {
      padding: 20px;
    }
    
    .header-with-back {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  </style>